import React from 'react';
import {
  LuChartBar,
  LuX,
  LuCheck,
  LuTarget,
  LuUsers,
  LuShieldCheck,
  LuFileText,
  LuTrendingUp,
  LuArrowUp,
  LuArrowDown,
  LuArrowUpDown,
} from 'react-icons/lu';
import { FaWhatsapp, FaFacebook, FaGoogle } from 'react-icons/fa6';

const BEFORE_POINTS = [
  'Budget cepat habis',
  'Banyak klik tapi sedikit chat',
  'Biaya per customer mahal',
];

const AFTER_POINTS = [
  'Customer chat bertambah',
  'Biaya per customer lebih efisien',
  'Budget lebih optimal, ROAS meningkat',
];

const OVERVIEW_STATS = [
  {
    label: 'Total Chat',
    value: '1.500+',
    sub: 'Messaging Conversation',
    trend: '',
    icon: FaWhatsapp,
  },
  {
    label: 'Average Cost per Result',
    value: 'Rp2.789',
    sub: '↓ 60% lebih efisien vs rata-rata sebelumnya',
    trend: 'down',
  },
  {
    label: 'CTR (All)',
    value: '3,18%',
    sub: '↑ 42% lebih tinggi vs rata-rata sebelumnya',
    trend: 'up',
  },
  {
    label: 'Conversion Rate',
    value: '5,62%',
    sub: 'Peningkatan signifikan dari strategi optimasi',
    trend: 'up',
  },
];

const CAMPAIGNS = [
  {
    no: 1,
    name: 'Layanan Detailing Interior Mobil',
    tag: 'Jasa Otomotif',
    image: '/campaigns/detailing.png',
    budget: 'Rp4.900.000',
    chat: '753',
    cost: 'Rp4.065',
    results: '753',
    reach: '78,370',
    ctr: '2.50%',
    note: 'Biaya per chat efisien untuk industri jasa otomotif',
  },
  {
    no: 2,
    name: 'Gosyen Nagih Kriuk',
    tag: 'Kuliner',
    image: '/campaigns/gosyen.jpg',
    budget: 'Rp175.000',
    chat: '127',
    cost: 'Rp1.371',
    results: '127',
    reach: '10,359',
    ctr: '5.78%',
    note: 'Budget kecil pun bisa hasilkan banyak chat',
  },
  {
    no: 3,
    name: 'Pembaca Isi Hati',
    tag: 'Pendidikan',
    image: '/campaigns/pembaca.png',
    budget: 'Rp3.150.000',
    chat: '495',
    cost: 'Rp6.162',
    results: '495',
    reach: '23,670',
    ctr: '2.32%',
    note: 'Strategi tepat bantu hasilkan chat berkualitas',
  },
];

const FOOTER_STRIP = [
  { icon: LuTarget, title: 'Strategi Terarah', sub: 'Sesuai Tujuan Bisnis' },
  { icon: LuUsers, title: 'Audience Tepat', sub: 'Lebih Banyak Customer' },
  { icon: LuTrendingUp, title: 'Optimasi Berkala', sub: 'Berbasis Data' },
  { icon: LuShieldCheck, title: 'Kontrol Budget', sub: 'Aman & Transparan' },
  { icon: LuFileText, title: 'Laporan Performa', sub: 'Mudah Dipahami' },
];

function MiniTableHeader({ children, icon: Icon = LuArrowUpDown }) {
  return (
    <th className="px-2 py-1.5 font-semibold text-slate-500">
      <span className="flex items-center gap-1">
        {children}
        <Icon className="h-2.5 w-2.5 text-slate-400" />
      </span>
    </th>
  );
}

const CaseStudy2Section = () => {
  return (
    <section className="relative overflow-hidden bg-white px-4 py-14 md:px-6 lg:px-10 lg:py-20">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-32 left-0 h-[550px] w-[550px] rounded-full bg-amber-100/60 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-72 w-72 rounded-full bg-slate-100/70 blur-3xl" />

      <div className="relative mx-auto max-w-7xl">
        {/* Badge */}
        <span className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-4 py-1.5 text-xs font-extrabold uppercase tracking-wide text-[#0F172A]">
          <LuChartBar className="h-3.5 w-3.5" />
          Case Study 2
        </span>

        {/* Header row: headline + performance overview */}
        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1.6fr_1fr] lg:gap-10">
          <div>
            <h2 className="font-[var(--font-display)] text-2xl font-black uppercase leading-[1.15] tracking-tight text-[#0F172A] sm:text-3xl lg:text-[2.3rem]">
              Bukan Besar Budgetnya,
              <br />
              <span className="text-amber-500">Tapi Seberapa Efisien Strategi Iklannya</span>
            </h2>
            <div className="mt-3 h-1.5 w-16 rounded-full bg-amber-400" />
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
              SriDesain membantu bisnis mengoptimalkan budget iklan agar mendapatkan
              lebih banyak peluang customer dengan biaya yang{' '}
              <span className="font-semibold text-amber-600">
                lebih efisien dan terukur.
              </span>
            </p>
          </div>

          {/* Performance overview - signature navy card */}
          <div className="relative overflow-hidden rounded-2xl bg-[#0F172A] p-5 shadow-xl shadow-slate-300/40">
            <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-amber-400/10 blur-2xl" />
            <p className="text-xs font-bold uppercase tracking-wide text-white">
              Performance Overview
            </p>
            <div className="relative mt-4 grid grid-cols-2 gap-3">
              {OVERVIEW_STATS.map((s) => (
                <div key={s.label} className="rounded-xl bg-white/5 p-3">
                  <p className="text-[10px] leading-tight text-slate-400">{s.label}</p>
                  <p
                    className={`mt-1 text-base font-extrabold ${
                      s.trend === 'down' ? 'text-green-400' : s.trend === 'up' ? 'text-green-400' : 'text-amber-400'
                    }`}
                  >
                    {s.value}
                  </p>
                  <p className="mt-0.5 text-[9px] leading-tight text-slate-400">{s.sub}</p>
                </div>
              ))}
            </div>
            <div className="relative mt-4 flex items-center justify-between border-t border-white/10 pt-4">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Platform
              </p>
              <div className="flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
                  <FaFacebook className="h-4 w-4 text-[#1877F2]" />
                </span>
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
                  <FaGoogle className="h-4 w-4 text-[#EA4335]" />
                </span>
              </div>
            </div>
            <p className="relative mt-2 text-center text-[9px] text-slate-500">vs last 30 days</p>
          </div>
        </div>

        {/* Before / After comparison */}
        <div className="relative mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* VS divider */}
          <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 sm:flex">
            <span className="flex h-12 w-12 items-center justify-center rounded-full border-4 border-white bg-[#0F172A] text-sm font-extrabold text-amber-400 shadow-lg">
              VS
            </span>
          </div>

          {/* Before card */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/40">
            <span className="inline-block rounded-full bg-red-100 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wide text-red-600">
              Sebelum
            </span>
            <p className="mt-3 text-base font-bold text-[#0F172A]">
              Budget Habis, Hasil Minim
            </p>

            <div className="relative mt-4 aspect-[8/7] w-full overflow-hidden rounded-xl">
              <img
                src="/case-study2-before.png"
                alt="Kondisi sebelum strategi iklan dioptimalkan"
                className="h-full w-full object-cover"
              />
            </div>

            <ul className="mt-4 flex flex-col gap-2">
              {BEFORE_POINTS.map((point) => (
                <li key={point} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-500">
                    <LuX className="h-2.5 w-2.5" strokeWidth={3} />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* After card */}
          <div className="rounded-2xl border border-amber-300 bg-amber-50/40 p-5 shadow-sm shadow-amber-100/60">
            <span className="inline-block rounded-full bg-green-100 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wide text-green-700">
              Sesudah
            </span>
            <p className="mt-3 text-base font-bold text-[#0F172A]">
              Strategi Tepat, Customer Bertambah
            </p>

            <div className="relative mt-4 aspect-[8/7] w-full overflow-hidden rounded-xl">
              <img
                src="/case-study2-after.png"
                alt="Kondisi sesudah strategi iklan dioptimalkan"
                className="h-full w-full object-cover"
              />
              <div className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[11px] font-semibold text-[#0F172A] shadow-md">
                <FaWhatsapp className="h-3.5 w-3.5 text-green-500" />
                23 Chat Baru
              </div>
            </div>

            <ul className="mt-4 flex flex-col gap-2">
              {AFTER_POINTS.map((point) => (
                <li key={point} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                    <LuCheck className="h-2.5 w-2.5" strokeWidth={3} />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Callout banner - signature navy strip */}
        <div className="relative mt-10 flex items-center gap-4 overflow-hidden rounded-2xl bg-[#0F172A] px-6 py-5 shadow-lg shadow-slate-300/40">
          <div className="pointer-events-none absolute -left-6 -top-6 h-32 w-32 rounded-full bg-amber-400/10 blur-2xl" />
          <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-400 text-[#0F172A]">
            <LuTarget className="h-5 w-5" />
          </span>
          <p className="relative text-sm font-bold leading-snug text-white sm:text-base">
            Hasil Iklan Bukan Ditentukan dari Besarnya Budget,{' '}
            <span className="text-amber-400">Tapi dari Strategi yang Digunakan.</span>
          </p>
        </div>

        {/* Campaign mini cards */}
        <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-3">
          {CAMPAIGNS.map((c) => (
            <div
              key={c.name}
              className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm shadow-slate-200/40"
            >
              <div className="flex items-start gap-2.5">
                <div className="relative shrink-0">
                  <img
                    src={c.image}
                    alt={c.name}
                    className="h-11 w-11 rounded-lg border border-slate-200 object-cover"
                  />
                  <span className="absolute -left-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-400 text-[9px] font-extrabold text-[#0F172A]">
                    {c.no}
                  </span>
                </div>
                <div>
                  <p className="text-[13px] font-bold leading-tight text-[#0F172A]">{c.name}</p>
                  <span className="mt-1 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600">
                    {c.tag}
                  </span>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3">
                <div>
                  <p className="text-[9px] text-slate-400">Budget</p>
                  <p className="text-xs font-bold text-[#0F172A]">{c.budget}</p>
                </div>
                <div>
                  <p className="text-[9px] text-slate-400">Chat Didapat</p>
                  <p className="text-xs font-bold text-[#0F172A]">{c.chat}</p>
                </div>
                <div className="rounded-lg bg-amber-50 px-2 py-1 text-center">
                  <p className="text-[9px] text-slate-500">Cost per Result</p>
                  <p className="text-xs font-extrabold text-amber-600">{c.cost}</p>
                  <p className="text-[8px] text-slate-400">/ Chat</p>
                </div>
              </div>

              <div className="mt-3 overflow-hidden rounded-lg border border-slate-100">
                <table className="w-full text-left text-[10px]">
                  <thead>
                    <tr className="bg-slate-50">
                      <MiniTableHeader icon={LuArrowDown}>
                        <span className="text-amber-600">Results</span>
                      </MiniTableHeader>
                      <MiniTableHeader>Reach</MiniTableHeader>
                      <MiniTableHeader>CTR (all)</MiniTableHeader>
                      <MiniTableHeader>
                        Cost per
                        <br />
                        result
                      </MiniTableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white">
                      <td className="px-2 py-1.5">
                        <span className="flex items-center gap-1 font-bold text-[#0F172A]">
                          {c.results}
                        </span>
                        <span className="text-[8px] text-slate-400">Messaging Conv..</span>
                      </td>
                      <td className="px-2 py-1.5 text-slate-600">{c.reach}</td>
                      <td className="px-2 py-1.5 text-slate-600">{c.ctr}</td>
                      <td className="px-2 py-1.5">
                        <span className="font-semibold text-[#0F172A] underline decoration-amber-300 decoration-2 underline-offset-2">
                          {c.cost}
                        </span>
                        <br />
                        <span className="text-[8px] text-slate-400">Per Messaging Conv.</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-600">
                <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                  <LuCheck className="h-2 w-2" strokeWidth={3} />
                </span>
                {c.note}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom strip */}
        <div className="mt-10 grid grid-cols-1 gap-5 rounded-2xl border border-slate-100 bg-white px-6 py-5 shadow-sm shadow-slate-200/40 sm:grid-cols-2 lg:grid-cols-5">
          {FOOTER_STRIP.map(({ icon: Icon, title, sub }) => (
            <div key={title} className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                <Icon className="h-5 w-5" />
              </span>
              <p className="leading-tight">
                <span className="block text-sm font-bold text-[#0F172A]">{title}</span>
                <span className="block text-xs text-slate-500">{sub}</span>
              </p>
            </div>
          ))}
        </div>

        {/* Footer statement */}
        <p className="mt-8 text-center text-sm font-bold uppercase tracking-wide text-[#0F172A] sm:text-base">
          Setiap Rupiah Budget Anda, Kami Maksimalkan Untuk{' '}
          <span className="text-amber-600">Hasil Terbaik.</span>
        </p>
      </div>
    </section>
  );
};

export default CaseStudy2Section;